import { Link, type LinkProps } from '@tanstack/react-router'
import { ArrowRight, Monitor } from 'lucide-react'
import type { ReactNode } from 'react'

import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { useIsAtLeast } from '@/lib/use-viewport'

/**
 * The screens that refuse rather than degrade — see docs/mobile-policy.md.
 *
 * Elsewhere a cramped layout is a poor experience. Here a cramped teller screen is a mis-keyed
 * cash amount against a real customer's account, found at end-of-day reconciliation, so the
 * screens where that is the failure mode ask for a wider display instead of shrinking into one.
 *
 * The decision is width, never the user agent: a phone in landscape, a tablet, and a half-width
 * window on a laptop are the same question. Sniffing the device would answer a different one.
 *
 * The panel is not an error and not an apology. Nothing has gone wrong, the operator has done
 * nothing incorrect, and there is a next step — so it says which screen, why once, and where to
 * go instead, in that order.
 */
export interface RequiresWiderScreenProps {
  /** The screen, named the way the navigation names it. */
  screen: string
  /** Why this one needs the room, in a single line. */
  reason: string
  /** Somewhere worth going from a phone. The dashboard reaches every module the user holds. */
  backTo?: LinkProps['to']
  backLabel?: string
  children: ReactNode
}

export function RequiresWiderScreen({
  screen,
  reason,
  backTo = '/',
  backLabel = 'Go to the dashboard',
  children,
}: RequiresWiderScreenProps) {
  // The `lg` breakpoint, because that is where the dense tables stop needing a horizontal scroll
  // to be read.
  const isWide = useIsAtLeast('lg')

  if (isWide) {
    return <>{children}</>
  }

  return (
    <Card className="mx-auto w-full max-w-md">
      <div className="flex flex-col items-start gap-4 p-5">
        <span
          className="flex size-9 items-center justify-center rounded-control bg-neutral-subtle text-muted"
          aria-hidden
        >
          <Monitor className="size-5" />
        </span>

        <div className="space-y-1.5">
          <h1 className="text-base font-semibold text-heading">
            {screen} needs a wider display
          </h1>
          <p className="text-sm text-body">{reason}</p>
        </div>

        <p className="text-xs text-muted">
          Open it on a desktop, or make this window wider. Customer records, approvals, and the
          dashboard are all built to be read at this size.
        </p>

        <Button asChild variant="secondary">
          <Link to={backTo}>
            {backLabel}
            <ArrowRight />
          </Link>
        </Button>
      </div>
    </Card>
  )
}
